import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/common/ui/alert-dialog";
import { ApiError } from "@/lib/api-client";

import { removeMembership } from "../api/organization-api";
import type { Member } from "../types";

interface RemoveMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: Member;
  onRemoved: () => void;
}

export function RemoveMemberDialog({
  open,
  onOpenChange,
  member,
  onRemoved,
}: RemoveMemberDialogProps): React.ReactElement {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setIsSubmitting(false);
    }
  }, [open]);

  const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      await removeMembership(member.id);
      onRemoved();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setError("You don't have permission to remove this member.");
        } else if (err.status === 404) {
          setError("Member no longer exists.");
        } else {
          setError("Something went wrong. Please try again.");
        }
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (isSubmitting) return;
        onOpenChange(nextOpen);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove member?</AlertDialogTitle>
          <AlertDialogDescription>
            <strong>{member.name}</strong> ({member.email}) will lose access to this organization
            unit. This action soft-deletes the membership and can be restored by re-inviting.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <div
            data-testid="remove-member-error"
            className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="remove-member-cancel" disabled={isSubmitting}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="remove-member-confirm"
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isSubmitting}
            onClick={handleConfirm}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Removing...
              </>
            ) : (
              "Remove"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
